import React, { useState, useMemo, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BreadcrumbProps,
  Button,
  Card,
  CardBody,
  Icon,
  Select,
  SelectOptionType,
  Tabs,
  Tooltip
} from '@aidkitorg/component-library';
import { AdminPanelPage } from '../AdminPanelPage';
import { useLocalizedStrings } from '../../Localization';
import { usePost } from '../../API';
import InterfaceContext from '../../Context';
import { SupportRequest, stages } from '../../SupportUtil';
import { SupportCaseList } from './components/SupportCaseList';
import { Pagination } from './components/Pagination';
import { SupportCasesResponse } from './types';
import { useSupportCaseActions } from './hooks';

/**
 * SupportPage - Main support case management page
 */
export const SupportPage = () => {
  const L = useLocalizedStrings();
  const context = useContext(InterfaceContext);
  const fetchSupportCasesAPI = usePost('/applicants/support');

  // Pagination and tab state
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'unclaimed' | 'claimed' | 'resolved'>('unclaimed');

  // Filters state
  const [stageFilter, setStageFilter] = useState<SelectOptionType | undefined>();
  const [tagsFilter, setTagsFilter] = useState<SelectOptionType | undefined>();
  const [assigneeFilter, setAssigneeFilter] = useState<SelectOptionType | undefined>();
  const [statusFilter, setStatusFilter] = useState<SelectOptionType | undefined>();

  // Reset to page 1 when tab or filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, stageFilter, tagsFilter, assigneeFilter, statusFilter]);

  // Convert stage filter label to backend format
  const stageFilterValue = React.useMemo(() => {
    if (!stageFilter) return undefined;
    const stageMap: Record<string, 'no_application' | 'in_progress' | 'applied'> = {
      'No Application': 'no_application',
      'In Progress': 'in_progress',
      'Applied': 'applied'
    };
    return stageMap[stageFilter.label as string];
  }, [stageFilter]);

  // Build filters object for API
  const filters = React.useMemo(() => ({
    ...(statusFilter && { status: statusFilter.value as any }),
    ...(tagsFilter && { tags: [tagsFilter.value as string] }),
    ...(assigneeFilter && { assignee: assigneeFilter.value as string }),
    ...(stageFilterValue && { stageFilter: stageFilterValue })
  }), [statusFilter, tagsFilter, assigneeFilter, stageFilterValue]);

  // Fetch support cases with TanStack Query
  const {
    data: supportCasesData,
    isLoading,
    error,
    refetch,
  } = useQuery<SupportCasesResponse>({
    queryKey: ["supportCases", activeTab, currentPage, filters],
    queryFn: async () => {
      const response: any = await fetchSupportCasesAPI({
        channel: "default",
        includeResolved: true,
        tabFilter: activeTab,
        page: currentPage,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
      });

      // Check if the response contains an error field
      if (response.error) {
        throw new Error(response.error);
      }

      return response as SupportCasesResponse;
    },
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (formerly cacheTime)
    refetchInterval: 2 * 60 * 1000, // Auto-refresh every 2 minutes
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData, // Show old data while fetching new page
  });

  // Log errors to console for debugging
  if (error) {
    console.error('Error fetching support cases:', error);
  }

  // Support case actions hook
  const { handleClaimCase, handleCaseClick } = useSupportCaseActions(refetch);

  // Backend-filtered support requests for current tab
  const supportRequests = supportCasesData?.supportRequests || [];
  const pagination = supportCasesData?.pagination;
  const tabCounts = supportCasesData?.tabCounts;

  // Extract available tags from support requests
  const availableTags = useMemo(() => {
    const tagsSet = new Set<string>();
    supportRequests.forEach((req: SupportRequest) => {
      if (req.row.tags && Array.isArray(req.row.tags)) {
        req.row.tags.forEach((tag: string) => tagsSet.add(tag));
      }
    });
    return Array.from(tagsSet)
      .sort()
      .map(tag => ({ label: tag, value: tag }));
  }, [supportRequests]);

  // Extract available assignees from backend filter options
  const availableAssignees = useMemo(() => {
    const assignees = new Set<string>();
    supportRequests.forEach((req: SupportRequest) => {
      if (req.row.assigned_to_name) {
        assignees.add(req.row.assigned_to_name);
      }
    });

    return Array.from(assignees)
      .sort()
      .map(assignee => ({ label: assignee, value: assignee }));
  }, [supportRequests]);

  // Stage filter options
  const stageOptions: SelectOptionType[] = stages.map(stage => ({
    label: stage,
    value: stage.toLowerCase().replace(/ /g, '_')
  }));

  // Status filter options
  const statusOptions: SelectOptionType[] = [
    { label: L.admin_panel.support.status_open, value: 'open' },
    { label: L.admin_panel.support.status_escalated, value: 'escalated' },
    { label: L.admin_panel.support.status_viewed, value: 'viewed' },
    { label: L.admin_panel.support.status_resolved, value: 'resolved' }
  ];

  // Breadcrumbs
  const breadcrumbs: BreadcrumbProps['items'] = [
    {
      label: L.admin_panel.nav.support
    }
  ];

  const tabs = useMemo(() => [
    {
      key: 'unclaimed',
      name: L.admin_panel.support.unclaimed,
      ...(tabCounts && {
        count: {
          value: tabCounts.unclaimed ?? 0,
          level: (tabCounts.unclaimed ?? 0) > 0 ? ('warning' as const) : ('info' as const)
        }
      }),
      content: (
        <div className="mt-3">
          <SupportCaseList
            tickets={supportRequests}
            onClaim={handleClaimCase}
            onTicketClick={handleCaseClick}
            emptyMessage={L.admin_panel.support.no_unclaimed_cases}
            loading={isLoading}
            error={!!error}
          />
          {pagination && !isLoading && !error && (
            <Pagination
              currentPage={pagination.currentPage}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              hasNextPage={pagination.hasNextPage}
              hasPreviousPage={pagination.hasPreviousPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )
    },
    {
      key: 'claimed',
      name: L.admin_panel.support.claimed,
      ...(tabCounts && {
        count: {
          value: tabCounts.claimed ?? 0,
          level: 'info' as const
        }
      }),
      content: (
        <div className="mt-3">
          <SupportCaseList
            tickets={supportRequests}
            onClaim={handleClaimCase}
            onTicketClick={handleCaseClick}
            emptyMessage={L.admin_panel.support.no_claimed_cases}
            loading={isLoading}
            error={!!error}
          />
          {pagination && !isLoading && !error && (
            <Pagination
              currentPage={pagination.currentPage}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              hasNextPage={pagination.hasNextPage}
              hasPreviousPage={pagination.hasPreviousPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )
    },
    {
      key: 'resolved',
      name: L.admin_panel.support.resolved,
      ...(tabCounts && {
        count: {
          value: tabCounts.resolved ?? 0,
          level: 'info' as const
        }
      }),
      content: (
        <div className="mt-3">
          <SupportCaseList
            tickets={supportRequests}
            onClaim={handleClaimCase}
            onTicketClick={handleCaseClick}
            emptyMessage={L.admin_panel.support.no_resolved_cases}
            loading={isLoading}
            error={!!error}
          />
          {pagination && !isLoading && !error && (
            <Pagination
              currentPage={pagination.currentPage}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              hasNextPage={pagination.hasNextPage}
              hasPreviousPage={pagination.hasPreviousPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )
    }
  ], [L, activeTab, pagination, tabCounts, supportRequests, handleClaimCase, handleCaseClick, isLoading, error, setCurrentPage]);

  return (
    <AdminPanelPage
      title={supportCasesData?.name?.[context.lang] || supportCasesData?.name?.en || L.admin_panel.nav.support}
      description={supportCasesData?.description?.[context.lang] || supportCasesData?.description?.en || ''}
      breadcrumbs={breadcrumbs}
      pageActionButtons={[
        <div key="stage-filter" className="w-48">
          <Select
            hideLabel
            label={L.admin_panel.support.stage_filter_label}
            description={L.admin_panel.support.stage_filter_description}
            placeholder={L.admin_panel.support.stage_filter_placeholder}
            value={stageFilter}
            onChange={(value) => {
              // If value is null or clicking the same option, deselect it
              if (!value || stageFilter?.value === value.value) {
                setStageFilter(undefined);
              } else {
                setStageFilter(value);
              }
            }}
            options={stageOptions}
          />
        </div>,
        <div key="status-filter" className="w-48">
          <Select
            hideLabel
            label={L.admin_panel.support.status_filter_label}
            description={L.admin_panel.support.status_filter_description}
            placeholder={L.admin_panel.support.status_filter_placeholder}
            value={statusFilter}
            onChange={(value) => {
              // If value is null or clicking the same option, deselect it
              if (!value || statusFilter?.value === value.value) {
                setStatusFilter(undefined);
              } else {
                setStatusFilter(value);
              }
            }}
            options={statusOptions}
          />
        </div>,
        <div key="tags-filter" className="w-48">
          <Select
            hideLabel
            label={L.admin_panel.support.tags_filter_label}
            description={L.admin_panel.support.tags_filter_description}
            placeholder={L.admin_panel.support.tags_filter_placeholder}
            value={tagsFilter}
            onChange={(value) => {
              // If value is null or clicking the same option, deselect it
              if (!value || tagsFilter?.value === value.value) {
                setTagsFilter(undefined);
              } else {
                setTagsFilter(value);
              }
            }}
            options={availableTags}
            disabled={availableTags.length === 0}
          />
        </div>,
        <div key="assignee-filter" className="w-48">
          <Select
            hideLabel
            label={L.admin_panel.support.assignee_filter_label}
            description={L.admin_panel.support.assignee_filter_description}
            placeholder={L.admin_panel.support.assignee_filter_placeholder}
            value={assigneeFilter}
            onChange={(value) => {
              // If value is null or clicking the same option, deselect it
              if (!value || assigneeFilter?.value === value.value) {
                setAssigneeFilter(undefined);
              } else {
                setAssigneeFilter(value);
              }
            }}
            options={availableAssignees}
            disabled={availableAssignees.length === 0}
          />
        </div>,
        <Tooltip text={L.admin_panel.support.refresh} asChild key="refresh">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => refetch()}
            icon={<Icon role="decorative" type="solid" name="ArrowPathIcon" />}
            aria-label={L.admin_panel.support.refresh}
          />
        </Tooltip>
      ]}
    >
      <Card className="rounded-md w-100 h-fit">
        <CardBody>
          <Tabs
            tabs={tabs}
            selectedTab={activeTab}
            justify="left"
            onChange={(tabKey: string) => setActiveTab(tabKey as 'unclaimed' | 'claimed' | 'resolved')}
          />
        </CardBody>
      </Card>
    </AdminPanelPage>
  );
};
